import type { MusicVisualFrame } from "../game/audio";
import type {
  BackgroundBloomMode,
  BackgroundGlowMode,
  BackgroundMixMode,
  BackgroundMotionMode,
  BackgroundSaturationMode,
  BackgroundStyleMode,
  GamePhase,
  SideFxDensityMode,
  SideFxMode
} from "../game/types";
import {
  RAVE_BORDER_DEFAULTS,
  clampRaveBorderWidthPx,
  clampRaveFlicker,
  getRaveBorderLevelConfig,
  type RaveBorderLevelConfig
} from "./raveBorderConfig";
import { ThreeSideFxRenderer } from "./threeSideFx";

const DEFAULT_AUDIO_DT_SEC = 1 / 60;

export interface RaveStripBounds {
  left: {
    x: number;
    width: number;
  };
  right: {
    x: number;
    width: number;
  };
  borderWidthPx: number;
}

export interface RaveAudioReactiveState {
  energy: number;
  transient: number;
  rawEnergy: number;
}

export interface RaveBorderRuntimeConfig extends RaveBorderLevelConfig {
  intensityBoost: number;
}

export interface ShellRaveBorderFrameInput {
  active: boolean;
  phase: GamePhase;
  level: number;
  songTimeSec: number;
  grooveActive: boolean;
  sideFxMode: SideFxMode;
  sideFxDensityMode: SideFxDensityMode;
  backgroundStyleMode: BackgroundStyleMode;
  backgroundBloomMode: BackgroundBloomMode;
  backgroundGlowMode: BackgroundGlowMode;
  backgroundSaturationMode: BackgroundSaturationMode;
  backgroundMixMode: BackgroundMixMode;
  backgroundMotionMode: BackgroundMotionMode;
  musicEnabled: boolean;
  musicVisual: MusicVisualFrame | null;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function resolveRaveBorderConfig(level: number): RaveBorderLevelConfig {
  return getRaveBorderLevelConfig(level);
}

export function computeRaveBeatPulse(
  timeSeconds: number,
  bpm: number,
  beatDiv: number,
  pulseSharpness: number
): number {
  const beat = timeSeconds * Math.max(1, bpm) / 60;
  const phase = ((beat * Math.max(0.25, beatDiv)) % 1 + 1) % 1;
  return Math.pow(Math.max(0, 1 - phase), Math.max(1, pulseSharpness));
}

export function applyGrooveOverrides(
  config: RaveBorderLevelConfig,
  grooveActive: boolean
): RaveBorderRuntimeConfig {
  if (!grooveActive) {
    return {
      ...config,
      flicker: clampRaveFlicker(config.flicker),
      intensityBoost: 1
    };
  }
  return {
    ...config,
    glow: config.glow * 1.12,
    outerGlow: config.outerGlow * 1.10,
    pulseStrength: config.pulseStrength * 1.14,
    flicker: clampRaveFlicker(config.flicker + 0.03),
    audioEnergyMult: config.audioEnergyMult * 1.08,
    intensityBoost: RAVE_BORDER_DEFAULTS.grooveIntensityMult
  };
}

export function deriveAudioReactiveState(
  musicVisual: MusicVisualFrame | null,
  musicEnabled: boolean,
  prevState: RaveAudioReactiveState | null,
  dt: number
): RaveAudioReactiveState {
  const previous: RaveAudioReactiveState = prevState ?? { energy: 0, transient: 0, rawEnergy: 0 };
  const safeDt = Math.max(0, dt);
  const raw = !musicEnabled || musicVisual == null
    ? 0
    : clamp01(musicVisual.rms * 0.55 + musicVisual.bass * 0.30 + musicVisual.treble * 0.15);

  const attackPerSec = 15;
  const releasePerSec = 3.2;
  const blend = raw >= previous.energy
    ? clamp01(attackPerSec * safeDt)
    : clamp01(releasePerSec * safeDt);
  const energy = clamp01(previous.energy + (raw - previous.energy) * blend);

  const delta = raw - previous.rawEnergy;
  const threshold = 0.09;
  const transientImpulse = delta > threshold
    ? clamp01((delta - threshold) / 0.25)
    : 0;
  const transientDecayPerSec = 3.8;
  const transient = musicEnabled
    ? Math.max(transientImpulse, Math.max(0, previous.transient - transientDecayPerSec * safeDt))
    : 0;

  return { energy, transient, rawEnergy: raw };
}

export function resolveRaveStripBounds(
  shellWidth: number,
  canvasLeft: number,
  canvasWidth: number,
  borderWidthPx: number
): RaveStripBounds {
  const safeShellWidth = Math.max(1, shellWidth);
  const safeCanvasWidth = Math.max(1, canvasWidth);
  const boundedCanvasLeft = clamp(canvasLeft, 0, Math.max(0, safeShellWidth - safeCanvasWidth));
  // Legacy compatibility output; active coverage now follows full side gutters.
  const boundedBorderWidth = clampRaveBorderWidthPx(borderWidthPx);

  const leftX = 0;
  const leftWidth = clamp(boundedCanvasLeft, 0, safeShellWidth);
  const rightX = clamp(boundedCanvasLeft + safeCanvasWidth, 0, safeShellWidth);
  const rightWidth = clamp(safeShellWidth - rightX, 0, safeShellWidth);

  return {
    left: { x: leftX, width: leftWidth },
    right: { x: rightX, width: rightWidth },
    borderWidthPx: boundedBorderWidth
  };
}

export function shouldDisableSideFxForMobilePortrait(
  shellWidth: number,
  shellHeight: number,
  pointerCoarse: boolean
): boolean {
  return pointerCoarse && shellHeight > shellWidth;
}

export class ShellRaveBorderFx {
  private readonly shell: HTMLDivElement;
  private readonly canvasWrapper: HTMLDivElement;
  private readonly overlayCanvas: HTMLCanvasElement;
  private readonly fallbackLeft: HTMLDivElement;
  private readonly fallbackRight: HTMLDivElement;
  private readonly threeFx: ThreeSideFxRenderer;

  private reducedMotion = false;
  private lowPower = false;
  private lastAudioUpdateSec = -Infinity;
  private levelFrom: RaveBorderLevelConfig = resolveRaveBorderConfig(1);
  private levelTo: RaveBorderLevelConfig = resolveRaveBorderConfig(1);
  private transitionStartSec = 0;
  private transitionMix = 1;
  private audioState: RaveAudioReactiveState = { energy: 0, transient: 0, rawEnergy: 0 };
  private songTimeOffsetSec = 0;
  private lastSourceSongTimeSec = Number.NaN;

  constructor(shell: HTMLDivElement, canvasWrapper: HTMLDivElement) {
    this.shell = shell;
    this.canvasWrapper = canvasWrapper;

    this.overlayCanvas = document.createElement("canvas");
    this.overlayCanvas.className = "shell-rave-overlay";
    this.overlayCanvas.style.touchAction = "none";
    this.overlayCanvas.style.pointerEvents = "none";
    this.shell.appendChild(this.overlayCanvas);

    this.fallbackLeft = document.createElement("div");
    this.fallbackLeft.className = "shell-rave-fallback-strip shell-rave-fallback-left";
    this.fallbackRight = document.createElement("div");
    this.fallbackRight.className = "shell-rave-fallback-strip shell-rave-fallback-right";
    this.shell.appendChild(this.fallbackLeft);
    this.shell.appendChild(this.fallbackRight);

    this.updateMotionPreferences();
    this.initMotionListener();

    this.threeFx = new ThreeSideFxRenderer(this.overlayCanvas);
  }

  isWebGlAvailable(): boolean {
    return this.threeFx.isReady();
  }

  private updateMotionPreferences(): void {
    const nav = typeof navigator === "undefined"
      ? null
      : navigator as Navigator & { deviceMemory?: number };
    const hardwareConcurrency = nav?.hardwareConcurrency ?? 8;
    const deviceMemory = nav?.deviceMemory ?? 8;
    this.lowPower = hardwareConcurrency <= 4 || deviceMemory <= 4;

    if (typeof window !== "undefined" && "matchMedia" in window) {
      const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion = mql.matches;
    } else {
      this.reducedMotion = false;
    }
  }

  private initMotionListener(): void {
    if (typeof window === "undefined" || !("matchMedia" in window)) {
      return;
    }
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const listener = (event: MediaQueryListEvent): void => {
      this.reducedMotion = event.matches;
    };
    mql.addEventListener("change", listener);
  }

  private updateFallbackStyles(
    visible: boolean,
    stripBounds: RaveStripBounds,
    config: RaveBorderRuntimeConfig,
    backgroundBloomMode: BackgroundBloomMode,
    backgroundGlowMode: BackgroundGlowMode,
    backgroundSaturationMode: BackgroundSaturationMode
  ): void {
    this.fallbackLeft.style.display = visible ? "block" : "none";
    this.fallbackRight.style.display = visible ? "block" : "none";
    if (!visible) {
      return;
    }
    this.fallbackLeft.style.left = `${stripBounds.left.x}px`;
    this.fallbackLeft.style.width = `${stripBounds.left.width}px`;
    this.fallbackRight.style.left = `${stripBounds.right.x}px`;
    this.fallbackRight.style.width = `${stripBounds.right.width}px`;

    const c0 = config.palette[0] ?? "#000000";
    const c1 = config.palette[1] ?? c0;
    const c2 = config.palette[2] ?? c1;
    const beatSeconds = 60 / Math.max(1, config.bpm);
    const durationSec = Math.max(0.24, beatSeconds * Math.max(1, config.beatDiv));
    this.shell.style.setProperty("--rave-fallback-c0", c0);
    this.shell.style.setProperty("--rave-fallback-c1", c1);
    this.shell.style.setProperty("--rave-fallback-c2", c2);
    this.shell.style.setProperty("--rave-fallback-duration", `${durationSec.toFixed(3)}s`);
    const bloomBrightness = backgroundBloomMode === "OFF"
      ? 0.92
      : backgroundBloomMode === "LOW"
        ? 1
        : backgroundBloomMode === "MEDIUM"
          ? 1.08
          : 1.16;
    const bloomOpacity = backgroundBloomMode === "OFF"
      ? 0.4
      : backgroundBloomMode === "LOW"
        ? 0.48
        : backgroundBloomMode === "MEDIUM"
          ? 0.56
          : 0.64;
    const glowMult = backgroundGlowMode === "LOW"
      ? 0.72
      : backgroundGlowMode === "MEDIUM"
        ? 1
        : 1.24;
    const saturation = backgroundSaturationMode === "LOW"
      ? 1.28
      : backgroundSaturationMode === "MEDIUM"
        ? 1.56
        : backgroundSaturationMode === "HIGH"
          ? 1.9
          : 2.2;
    this.shell.style.setProperty("--rave-fallback-brightness", bloomBrightness.toFixed(3));
    this.shell.style.setProperty("--rave-fallback-opacity", bloomOpacity.toFixed(3));
    this.shell.style.setProperty("--rave-fallback-glow", glowMult.toFixed(3));
    this.shell.style.setProperty("--rave-fallback-saturation", saturation.toFixed(3));
  }

  render(input: ShellRaveBorderFrameInput): void {
    const nowSec = performance.now() / 1000;
    const audioDt = Number.isFinite(this.lastAudioUpdateSec)
      ? clamp(nowSec - this.lastAudioUpdateSec, 0, 0.25)
      : DEFAULT_AUDIO_DT_SEC;
    this.lastAudioUpdateSec = nowSec;

    this.audioState = deriveAudioReactiveState(
      input.musicVisual,
      input.musicEnabled,
      this.audioState,
      audioDt
    );

    const nextConfig = resolveRaveBorderConfig(input.level);
    if (nextConfig.level !== this.levelTo.level) {
      this.levelFrom = this.levelTo;
      this.levelTo = nextConfig;
      this.transitionStartSec = nowSec;
      this.transitionMix = 0;
    }

    if (this.transitionMix < 1) {
      const transitionElapsed = Math.max(0, nowSec - this.transitionStartSec);
      this.transitionMix = clamp01(
        transitionElapsed / (RAVE_BORDER_DEFAULTS.transitionFadeMs / 1000)
      );
    }

    const fromRuntime = applyGrooveOverrides(this.levelFrom, input.grooveActive);
    const toRuntime = applyGrooveOverrides(this.levelTo, input.grooveActive);

    const shellRect = this.shell.getBoundingClientRect();
    const centerCanvasRect = this.canvasWrapper.getBoundingClientRect();
    const stripBounds = resolveRaveStripBounds(
      shellRect.width,
      centerCanvasRect.left - shellRect.left,
      centerCanvasRect.width,
      RAVE_BORDER_DEFAULTS.borderWidthPx
    );

    const active = input.active && shellRect.width > 0 && shellRect.height > 0;
    const pointerCoarse = typeof window !== "undefined" && "matchMedia" in window
      ? window.matchMedia("(pointer: coarse)").matches
      : false;
    const mobilePortraitBlocked = shouldDisableSideFxForMobilePortrait(
      shellRect.width,
      shellRect.height,
      pointerCoarse
    );
    const effectiveActive = active && !mobilePortraitBlocked;
    const subtleOpacity = input.sideFxMode === "SUBTLE" ? 0.62 : 1;
    this.overlayCanvas.style.display = effectiveActive ? "block" : "none";
    this.overlayCanvas.style.opacity = subtleOpacity.toString();
    this.fallbackLeft.style.opacity = (0.44 * subtleOpacity).toFixed(3);
    this.fallbackRight.style.opacity = (0.44 * subtleOpacity).toFixed(3);

    const sourceSongTimeSec = Number.isFinite(input.songTimeSec) ? Math.max(0, input.songTimeSec) : 0;
    if (Number.isFinite(this.lastSourceSongTimeSec)) {
      const driftSec = sourceSongTimeSec - this.lastSourceSongTimeSec;
      if (driftSec < -0.05) {
        this.songTimeOffsetSec += this.lastSourceSongTimeSec - sourceSongTimeSec;
      }
    }
    this.lastSourceSongTimeSec = sourceSongTimeSec;
    const stabilizedSongTimeSec = sourceSongTimeSec + this.songTimeOffsetSec;

    const rendered = this.threeFx.render({
      active: effectiveActive,
      grooveActive: input.grooveActive,
      shellWidth: shellRect.width,
      shellHeight: shellRect.height,
      stripBounds,
      songTimeSec: stabilizedSongTimeSec,
      sideFxMode: input.sideFxMode,
      sideFxDensityMode: input.sideFxDensityMode,
      backgroundStyleMode: input.backgroundStyleMode,
      backgroundBloomMode: input.backgroundBloomMode,
      backgroundGlowMode: input.backgroundGlowMode,
      backgroundSaturationMode: input.backgroundSaturationMode,
      backgroundMixMode: input.backgroundMixMode,
      backgroundMotionMode: input.backgroundMotionMode,
      reducedMotion: this.reducedMotion,
      lowPower: this.lowPower,
      transitionMix: this.transitionMix,
      fromConfig: fromRuntime,
      toConfig: toRuntime,
      audio: this.audioState
    });

    const fallbackVisible = effectiveActive && (!rendered || !this.threeFx.isReady());
    this.updateFallbackStyles(
      fallbackVisible,
      stripBounds,
      toRuntime,
      input.backgroundBloomMode,
      input.backgroundGlowMode,
      input.backgroundSaturationMode
    );
  }
}
